import React, { ReactNode, useEffect, useRef, useState } from "react";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import { mergeClasses } from "../utils/styles.util";

interface DropdownProps {
  label: string;
  selectedLabel: string;
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
  // Fires whenever the dropdown closes, however it closes (explicit
  // selection or an outside click) — lets children reset any in-progress
  // local state (e.g. an inline "new item" form) that would otherwise go
  // stale the next time the menu opens.
  onClose?: () => void;
  children: (helpers: { close: () => void }) => ReactNode;
}

const Dropdown: React.FC<DropdownProps> = ({ label, selectedLabel, icon, disabled, className, onClose, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(prev => {
          if (prev) onClose?.();
          return false;
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const close = () => {
    setIsOpen(false);
    onClose?.();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={mergeClasses(
          "flex items-center justify-between w-full border border-gray-500 rounded-md px-2 py-1",
          disabled ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-gray-50",
          className
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {icon && <div className="flex items-center">{icon}</div>}
          <span className="text-sm text-gray-600">{label}</span>
          <span className="text-sm font-medium">{selectedLabel}</span>
        </div>
        <div className={`flex items-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDownIcon />
        </div>
      </div>
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
          {children({ close })}
        </div>
      )}
    </div>
  )
};

export const DropdownOption: React.FC<{ onClick: () => void; children: ReactNode }> = ({ onClick, children }) => (
  <div onClick={onClick} className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0">
    {children}
  </div>
);

export default Dropdown;
